import React, { useState, useEffect, useRef } from 'react'
import { Moment } from 'moment'
import { Droppable, Draggable } from '@hello-pangea/dnd'
import { DroppedRecipe, MenuRecipe } from './index'
import PlusMinusWrapper from '../shared/plusMinusWrapper'

interface MenuListProps {
  selectedDate: Moment
  searchQuery: string
  droppedRecipes: DroppedRecipe[]
  fixedMenuItems: MenuRecipe[]
  fixedItemQuantityOverrides: Record<number, number>
  onRemoveDroppedRecipe: (recipeId: number) => void
  onRemoveFixedItem: (recipeId: number) => void
  onUpdateDroppedQuantity: (recipeId: number, newQuantity: number) => void
  onUpdateFixedQuantity: (recipeId: number, newQuantity: number) => void
  onCopyMenu: () => void
  onPasteMenu: () => void
  onSaveMenu: () => void
  hasCopiedItems: boolean
  loading: boolean
  menuLoading: boolean
  isDateInPast: boolean
  readOnly: boolean
}

export type UnifiedMenuItem =
  | { type: 'fixed'; data: MenuRecipe; index: number }
  | { type: 'dropped'; data: DroppedRecipe; index: number }

const MenuItemCard = ({
  menuRecipe,
  quantity,
  onUpdateQuantity,
  onRemove,
  readOnly
}: {
  menuRecipe: MenuRecipe
  quantity: number
  onUpdateQuantity: (recipeId: number, newQuantity: number) => void
  onRemove: (recipeId: number) => void
  readOnly: boolean
}) => {
  const handleQuantityChange = (name: string, newValue: number) => {
    if (newValue >= 1) {
      onUpdateQuantity(menuRecipe.recipe_id, newValue)
    }
  }

  const handleDelete = () => {
    onRemove(menuRecipe.recipe_id)
  }

  return (
    <div className="menu-item-card">
      <div className="menu-item-content">
        <div className="menu-item-image">
          <div className="menu-item-image-placeholder">
            <img src={menuRecipe.image_url} alt={menuRecipe.recipe_name} />
          </div>
        </div>
        <div className="menu-item-details">
          <h3 className="menu-item-name">{menuRecipe.recipe_name}</h3>
          {readOnly ? (
            <span className="quantity-input">{quantity}</span>
          ) : (
            <PlusMinusWrapper
              name="quantity"
              value={quantity}
              step={1}
              min={1}
              onChange={handleQuantityChange}
            >
              <div className='subwrapper'>
                <input
                  type="number"
                  value={quantity}
                  readOnly
                  className="quantity-input"
                />
              </div>
            </PlusMinusWrapper>
          )}
        </div>
      </div>
      {!readOnly && (
        <div className="menu-item-actions">
          <button className="delete-btn" onClick={handleDelete}>
            <i className="fa fa-trash"></i>
          </button>
        </div>
      )}
    </div>
  )
}

const DroppedRecipeCard = ({
  droppedRecipe,
  onRemove,
  onUpdateQuantity
}: {
  droppedRecipe: DroppedRecipe
  onRemove: (recipeId: number) => void
  onUpdateQuantity: (recipeId: number, newQuantity: number) => void
}) => {
  const handleQuantityChange = (name: string, newValue: number) => {
    if (newValue >= 1) {
      onUpdateQuantity(droppedRecipe.recipe_id, newValue)
    }
  }

  return (
    <div className="menu-item-card">
      <div className="menu-item-content">
        <div className="menu-item-image">
          <div className="menu-item-image-placeholder">
            {droppedRecipe.image_url ? (
              <img src={droppedRecipe.image_url} alt={droppedRecipe.name} />
            ) : (
              <i className="fa fa-cutlery"></i>
            )}
          </div>
        </div>
        <div className="menu-item-details">
          <h3 className="menu-item-name">{droppedRecipe.name}</h3>
          <PlusMinusWrapper
            name="quantity"
            value={droppedRecipe.quantity}
            step={1}
            min={1}
            onChange={handleQuantityChange}
          >
            <div className='subwrapper'>
              <input
                type="number"
                value={droppedRecipe.quantity}
                readOnly
                className="quantity-input"
              />
            </div>
          </PlusMinusWrapper>
        </div>
      </div>
      <div className="menu-item-actions">
        <button className="delete-btn" onClick={() => onRemove(droppedRecipe.recipe_id)}>
          <i className="fa fa-trash"></i>
        </button>
      </div>
    </div>
  )
}

const MenuList = ({
  selectedDate,
  searchQuery,
  droppedRecipes,
  fixedMenuItems,
  fixedItemQuantityOverrides,
  onRemoveDroppedRecipe,
  onRemoveFixedItem,
  onUpdateDroppedQuantity,
  onUpdateFixedQuantity,
  onCopyMenu,
  onPasteMenu,
  onSaveMenu,
  hasCopiedItems,
  loading,
  menuLoading,
  isDateInPast,
  readOnly
}: MenuListProps) => {
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false)
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleCopyClick = () => {
    onCopyMenu()
    setShowCopiedFeedback(true)

    // clear any existing timeout
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current)
    }

    // reset after 5 seconds
    copyTimeoutRef.current = setTimeout(() => {
      setShowCopiedFeedback(false)
    }, 5000)
  }

  // cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current)
      }
    }
  }, [])

  const query = searchQuery.trim().toLowerCase()
  const matchesQuery = (name: string) => query === '' || name.toLowerCase().includes(query)
  const filteredFixedItems = fixedMenuItems.filter(item => matchesQuery(item.recipe_name))
  const filteredDroppedRecipes = droppedRecipes.filter(item => matchesQuery(item.name))

  // Build a unified list by interleaving fixed items and dropped recipes based on position
  // Each dropped recipe has a 'position' field that indicates where it should appear in the unified list
  const unifiedList: UnifiedMenuItem[] = (() => {
    const result: UnifiedMenuItem[] = []
    let fixedIdx = 0

    // Sort dropped recipes by position
    const sortedDropped = [...filteredDroppedRecipes].sort((a, b) => a.position - b.position)
    let droppedIdx = 0

    // Iterate through unified positions
    for (let pos = 0; pos < fixedMenuItems.length + droppedRecipes.length; pos++) {
      // Add all dropped items that have this position
      while (droppedIdx < sortedDropped.length && sortedDropped[droppedIdx].position === pos) {
        result.push({ type: 'dropped' as const, data: sortedDropped[droppedIdx], index: result.length })
        droppedIdx++
      }

      // Add next fixed item if available
      if (fixedIdx < filteredFixedItems.length) {
        result.push({ type: 'fixed' as const, data: filteredFixedItems[fixedIdx], index: result.length })
        fixedIdx++
      }
    }

    // Add any remaining dropped items (positions beyond the loop range)
    while (droppedIdx < sortedDropped.length) {
      result.push({ type: 'dropped' as const, data: sortedDropped[droppedIdx], index: result.length })
      droppedIdx++
    }

    return result
  })()

  return (
    <Droppable droppableId="menu-dropzone">
      {(provided, snapshot) => (
        <div
          className={`menu-meals-area ${snapshot.isDraggingOver ? 'is-dragging-over' : ''}`}
          ref={provided.innerRef}
          {...provided.droppableProps}
        >
          <div className="menu-items-list droppable">
            {menuLoading ? (
              <div className="menu-initial-loading d-flex justify-content-center py-4">
                <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />
              </div>
            ) : unifiedList.length === 0 ? (
              <p className="no-meals-text">{readOnly ? 'No recipes on the menu' : 'Drag recipes here to add to menu'}</p>
            ) : (
              unifiedList.map((item, index) => (
                <Draggable
                  key={item.type === 'fixed' ? `fixed-${item.data.id}` : `dropped-${item.data.id}`}
                  draggableId={item.type === 'fixed' ? `fixed-${item.data.id}` : `dropped-${item.data.id}`}
                  index={index}
                  isDragDisabled={true}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="menu-item-wrapper task"
                    >
                      {item.type === 'fixed' ? (
                        <MenuItemCard
                          menuRecipe={item.data}
                          quantity={fixedItemQuantityOverrides[item.data.recipe_id] ?? item.data.amount}
                          onUpdateQuantity={onUpdateFixedQuantity}
                          onRemove={onRemoveFixedItem}
                          readOnly={readOnly}
                        />
                      ) : (
                        <DroppedRecipeCard
                          droppedRecipe={item.data}
                          onRemove={onRemoveDroppedRecipe}
                          onUpdateQuantity={onUpdateDroppedQuantity}
                        />
                      )}
                    </div>
                  )}
                </Draggable>
              ))
            )}
          </div>
          {provided.placeholder}

          {!readOnly && <div className="menu-actions-bar">
            <button className={"menu-action-btn save-btn " + (isDateInPast ? "in-past" : "")} onClick={onSaveMenu} disabled={loading || isDateInPast}>
              {loading ? (
                <>
                  <i className="fa fa-spinner fa-spin"></i>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <i className="fa fa-save {}"></i>
                  <span>Save menu</span>
                </>
              )}
            </button>
            <button className="menu-action-btn copy-btn" onClick={handleCopyClick} disabled={loading}>
              {showCopiedFeedback ? (
                <div className="copy-btn-content">
                  <i className="fa fa-check"></i>
                  <span>Copied</span>
                </div>
              ) : (
                <div className="copy-btn-content">
                  <i className="fa fa-copy"></i>
                  <span>Copy Menu items</span>
                </div>
              )}
            </button>
            {hasCopiedItems && !isDateInPast && (
              <button className="menu-action-btn paste-btn" onClick={onPasteMenu}>
                <i className="fa fa-paste"></i>
                <span>Paste menu items</span>
              </button>
            )}
          </div>}
        </div>
      )}
    </Droppable>
  )
}

export default MenuList
